import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import api from '../services/api';
import { Plus, Minus, Users, Clock, RefreshCw } from 'lucide-react';

export default function Admin() {
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [minuteInputs, setMinuteInputs] = useState({});
  const [updatingUser, setUpdatingUser] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/users');
      setUsers(response.data.users || []);
      const inputs = {};
      (response.data.users || []).forEach(u => {
        inputs[u.id] = 10;
      });
      setMinuteInputs(inputs);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Admin access required');
      } else {
        toast.error('Failed to fetch users');
      }
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateMinutes = async (userId, operation) => {
    const minutes = parseFloat(minuteInputs[userId]) || 0;
    if (minutes <= 0) {
      toast.error('Please enter a valid number of minutes');
      return;
    }

    setUpdatingUser(userId);
    try {
      const response = await api.post(`/admin/users/${userId}/minutes?minutes=${minutes}&operation=${operation}`);
      toast.success(`${operation === 'add' ? 'Added' : 'Removed'} ${minutes} minutes. New balance: ${response.data.new_balance.toFixed(1)}`);
      setUsers(prev => prev.map(u => 
        u.id === userId ? { ...u, minutes: response.data.new_balance } : u
      ));
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${operation} minutes`);
    } finally {
      setUpdatingUser(null);
    }
  };

  const handleInputChange = (userId, value) => {
    setMinuteInputs(prev => ({
      ...prev,
      [userId]: value
    }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto p-4 md:p-6 max-w-6xl">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 md:mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-primary to-purple-500 bg-clip-text text-transparent">
              Admin Panel
            </h1>
            <p className="text-sm md:text-base text-muted-foreground mt-1">Manage users and credits</p>
          </div>
          <Button 
            onClick={fetchUsers} 
            variant="outline"
            disabled={loading}
            className="gap-2 w-full sm:w-auto"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" />
              <CardTitle>All Users</CardTitle>
            </div>
            <CardDescription>
              {users.length} registered {users.length === 1 ? 'user' : 'users'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading && users.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : users.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">No users found</p>
            ) : (
              <div className="space-y-3">
                {users.map((user) => (
                  <div 
                    key={user.id} 
                    className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 bg-muted/50 rounded-lg border border-border/50"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">
                        {user.name || 'Unnamed User'}
                      </p>
                      <p className="text-sm text-muted-foreground truncate">
                        {user.email || 'No email'}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Joined: {formatDate(user.created_at)}
                      </p>
                    </div>
                    
                    <div className="flex items-center gap-2 bg-primary/10 px-3 py-2 rounded-lg">
                      <Clock className="h-4 w-4 text-primary" />
                      <span className="font-bold text-primary text-lg">
                        {typeof user.minutes === 'number' ? user.minutes.toFixed(1) : user.minutes}
                      </span>
                      <span className="text-sm text-muted-foreground">min</span>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                      <Input
                        type="number"
                        value={minuteInputs[user.id] || ''}
                        onChange={(e) => handleInputChange(user.id, e.target.value)}
                        className="w-16 sm:w-20 text-center"
                        min="0.1"
                        step="0.1"
                        placeholder="10"
                      />
                      <Button
                        size="sm"
                        onClick={() => handleUpdateMinutes(user.id, 'add')}
                        disabled={updatingUser === user.id}
                        className="bg-green-600 hover:bg-green-700 gap-1 flex-1 sm:flex-none"
                      >
                        <Plus className="h-4 w-4" />
                        <span className="hidden sm:inline">Add</span>
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleUpdateMinutes(user.id, 'remove')}
                        disabled={updatingUser === user.id}
                        className="gap-1 flex-1 sm:flex-none"
                      >
                        <Minus className="h-4 w-4" />
                        <span className="hidden sm:inline">Remove</span>
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
